// components/admin/AccountingDashboard.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { DollarSign, Users, Truck, Package, ChevronLeft, FileSpreadsheet, Wallet, BookOpen, QrCode } from 'lucide-react';
import DateSelector from './DateSelector';
import UsersAccounting from './UsersAccounting';
import ProvidersAccounting from './ProvidersAccounting';
import DriversAccounting from './DriversAccounting';
import PaymentsAccounting from './PaymentsAccounting';
import LedgerAccounting from './LedgerAccounting';
import PaymentMethodsModal from './PaymentMethodsModal';
import AgencyEntry, { useAgencyCapabilities } from '../../shared/agency/AgencyEntry';

const API_URL = process.env.REACT_APP_API_URL;

function AccountingDashboard({ userId, setActiveComponent }) {
  const agency = useAgencyCapabilities();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [isExporting, setIsExporting] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  function handleToggleCalendar() {
    setShowCalendar(!showCalendar);
  }

  async function handleExportAllDeliveries() {
    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/accounting/export-all-deliveries`,
        {
          admin_id: userId,
          date: selectedDate
        },
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_completo_${selectedDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting all deliveries:', error);
      alert('Error al exportar el reporte. Por favor intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  }

  const tabs = [
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'providers', label: 'Proveedores', icon: Package },
    { id: 'drivers', label: 'Conductores', icon: Truck },
    { id: 'payments', label: 'Pagos', icon: Wallet },
    { id: 'ledger', label: 'Ledger', icon: BookOpen },
    ...(agency?.enabled ? [{ id: 'agency', label: 'Agencias', icon: Package }] : []),

  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="p-3 bg-brand-primary rounded-xl">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 font-montserrat">
              Contabilidad
            </h1>
          </div>
          <p className="text-slate-600 font-montserrat">
            Gestión financiera y estadísticas de entregas
          </p>
        </div>

        {/* Date Selector and Export Button */}
        <div className="mb-6 flex flex-col items-stretch gap-4 lg:flex-row lg:items-start">
          <div className="w-full flex-shrink-0 sm:w-[320px]">
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              showCalendar={showCalendar}
              onToggleCalendar={handleToggleCalendar}
            />
          </div>
          
          <button
            onClick={handleExportAllDeliveries}
            disabled={isExporting}
            className="flex items-center gap-2 px-6 py-3 bg-brand-green text-white rounded-xl hover:bg-brand-green/90 transition-all font-montserrat font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className="w-5 h-5" />
            {isExporting ? 'Exportando...' : 'Reporte Completo'}
          </button>

          <button
            type="button"
            onClick={() => setShowPaymentMethods(true)}
            className="flex items-center gap-2 px-4 py-3 bg-brand-primary text-white rounded-xl hover:bg-brand-primary/90 transition-all font-montserrat font-bold shadow-sm"
            title="Administrar metodos de pago para conductores"
          >
            <QrCode className="w-5 h-5" />
            <span className="hidden md:inline">Metodos de pago</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex-1 flex items-center justify-center gap-2 px-6 py-4 font-montserrat font-semibold transition-all
                    ${activeTab === tab.id
                      ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-primary/5'
                      : 'text-slate-600 hover:text-brand-primary hover:bg-slate-50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'agency' && <AgencyEntry initialTab="finance" onBack={() => setActiveTab('ledger')} />}
            {activeTab === 'users' && (
              <UsersAccounting userId={userId} selectedDate={selectedDate} />
            )}
            {activeTab === 'providers' && (
              <ProvidersAccounting userId={userId} selectedDate={selectedDate} />
            )}
            {activeTab === 'drivers' && (
              <DriversAccounting userId={userId} selectedDate={selectedDate} />
            )}
            {activeTab === 'payments' && (
              <PaymentsAccounting userId={userId} selectedDate={selectedDate} />
            )}
            {activeTab === 'ledger' && (
              <LedgerAccounting userId={userId} selectedDate={selectedDate} />
            )}
          </div>
        </div>
      </div>
      <PaymentMethodsModal
        open={showPaymentMethods}
        onClose={() => setShowPaymentMethods(false)}
      />
    </div>
  );
}

export default AccountingDashboard;
